import React, { useState, useEffect } from "react";
import { KpiModal, ModalHeader, KpiIcon } from "../../Quantative/component/KpiIcons";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config";
import TabularDynamicForm from "./TabularDynamicForm";
import MultiDocumentUploadField from "../../components/MultiDocumentUploadField";
import ViewNoteButton, { noteToText } from "./ViewNoteButton";
import { conformGridToQuestion, countGridValues, classifyQuestionShape, columnsForRow } from "../services/answerShape";

const QualitativeAddDataModal = ({ show, onHide, question, financialYearId, matchedAnswer, onSave }) => {
    const [gridData, setGridData] = useState([]);
    const [columnGroups, setColumnGroups] = useState(null);
    const [rowNames, setRowNames] = useState([]);
    const [note, setNote] = useState("");
    const [showNoteEditor, setShowNoteEditor] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [attachments, setAttachments] = useState({ documentIds: [], uploadedDocuments: [] });
    // True while MultiDocumentUploadField is still uploading a newly-picked file —
    // Save must stay disabled, otherwise it can fire before the file has a
    // fileMetadataId and silently drop that attachment.
    const [docsUploading, setDocsUploading] = useState(false);

    const questionId = question?.questionId || question?.id;
    const resolvedFyId = financialYearId || question?.financial_year_id;
    // qualitativeQuestionAnswer already returns attached documents inline —
    // no separate /documents fetch needed.
    const existingDocuments = matchedAnswer?.documents ?? [];

    // Initialize data when modal opens
    useEffect(() => {
        if (!show) return;
        setError("");
        setShowNoteEditor(false);

        const {
            rowOptions,
            dynamicRowsDynamicColumns: isFullyDynamic,
            dynamicRowsFixedColumns: isDynamic,
        } = classifyQuestionShape(question?.details);

        if (matchedAnswer) {
            try {
                const raw = matchedAnswer.answer;
                const parsed = Array.isArray(raw) ? raw : JSON.parse(raw);
                let loadedGrid = Array.isArray(parsed) ? parsed : [];
                const answerIsGrouped = Array.isArray(loadedGrid?.[0]?.[0]);
                const loadedGroups = answerIsGrouped
                    && Array.isArray(matchedAnswer.columnGroups)
                    && matchedAnswer.columnGroups.length > 0
                    ? matchedAnswer.columnGroups
                    : null;

                if (isFullyDynamic && !loadedGroups) {
                    const predefinedRows = rowOptions[0]?.pre_defined_rows;
                    if (Array.isArray(predefinedRows) && predefinedRows.length > 0) {
                        const numCols = (loadedGrid[0] || []).length || 1;
                        const emptyCell = () => ({ value: "", unit_id: null });
                        while (loadedGrid.length < predefinedRows.length + 1) {
                            loadedGrid = [...loadedGrid, new Array(numCols).fill(null).map(emptyCell)];
                        }
                    }
                }
                const conformedGrid = conformGridToQuestion(loadedGrid, question?.details, loadedGroups);
                if (countGridValues(conformedGrid) < countGridValues(loadedGrid)) {
                    setError("The saved answer no longer matches this question's table layout. Entries outside the current rows/columns are hidden and will be removed when you save.");
                }
                setGridData(conformedGrid);
                setColumnGroups(loadedGroups);
                setRowNames(Array.isArray(matchedAnswer.rowNames) ? matchedAnswer.rowNames : []);

                if (matchedAnswer.note) {
                    if (typeof matchedAnswer.note === "string") {
                        setNote(matchedAnswer.note);
                    } else if (Array.isArray(matchedAnswer.note)) {
                        setNote(matchedAnswer.note.flat().join("\n"));
                    }
                } else {
                    setNote("");
                }
            } catch (e) {
                console.error("Failed to parse matched answer", e);
                setColumnGroups(null);
                setRowNames([]);
                initializeEmptyGrid(rowOptions, isDynamic, isFullyDynamic, question?.details || [], null);
            }
        } else {
            setColumnGroups(null);
            setRowNames([]);
            initializeEmptyGrid(rowOptions, isDynamic, isFullyDynamic, question?.details || [], null);
            setNote("");
        }
    }, [show, question, matchedAnswer]);

    const initializeEmptyGrid = (rowOptions, isDynamic, isFullyDynamic, details, columnGroups) => {
        const shape = classifyQuestionShape(details);
        const emptyCell = () => ({ value: "", unit_id: null });
        const hasGroups = Array.isArray(columnGroups) && columnGroups.length > 0;

        if (isFullyDynamic) {
            // row[0] = column headers (plain strings), row[1] = first data row (cell objects)
            const predefinedRows = rowOptions[0]?.pre_defined_rows;
            if (Array.isArray(predefinedRows) && predefinedRows.length > 0) {
                setGridData([[""], ...predefinedRows.map(() => [emptyCell()])]);
                setRowNames(new Array(predefinedRows.length).fill(""));
            } else {
                setGridData([[""], [emptyCell()]]);
                setRowNames([""]);
            }
        } else if (isDynamic) {
            const cols = shape.colOptions;
            if (hasGroups) {
                // 3-D: one row → [group0:[col0,col1,...], group1:[col0,col1,...], ...]
                setGridData([columnGroups.map(() => new Array(cols.length).fill(null).map(emptyCell))]);
            } else {
                setGridData([new Array(cols.length).fill(null).map(emptyCell)]);
            }
            setRowNames([""]);
        } else {
            const initialGrid = rowOptions.map((row, rIndex) => {
                const cols = columnsForRow(shape, rIndex);
                if (hasGroups) {
                    // 3-D row: gridData[rIndex][groupIdx][colIdx]
                    return columnGroups.map(() => new Array(cols.length).fill(null).map(emptyCell));
                }
                return new Array(cols.length).fill(null).map(emptyCell);
            });
            setGridData(initialGrid);
        }
    };

    const handleSave = async () => {
        const hasDocuments =
            (attachments.documentIds?.length || 0) > 0 ||
            (attachments.uploadedDocuments?.length || 0) > 0;
        if (countGridValues(gridData) === 0 && !note.trim() && !hasDocuments) {
            setError("Please fill in at least one value, add a note, or attach a document before saving — an empty answer cannot be submitted.");
            return;
        }
        setSaving(true);
        setError("");

        try {
            // Build payload
            // For qualitative answers, the endpoint is typically quantative-data but wait...
            // Qualitative usually hits postLogin/saveReportingAnswer
            const payload = {
                financialYearId: financialYearId || question.financial_year_id,
                questionId: question.questionId || question.id,
                questionTitle: question.title,
                moduleId: question.module_id || question.moduleId,
                answer: JSON.stringify(gridData),
                note: [[note]],
                questionType: question.question_type || question.questionType,
                frequency: question.frequency,
                status: "SUBMITTED",
                module: "qualitative",
                documentIds: attachments.documentIds,
                uploadedDocuments: attachments.uploadedDocuments,
                columnGroups: Array.isArray(columnGroups) ? columnGroups : [],
                rowNames: Array.isArray(rowNames) ? rowNames : [],
            };

            const { isSuccess, message } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}qualitativeQuestionAnswer`,
                {},
                payload,
                "POST"
            );

            if (isSuccess) {
                onSave && onSave(payload);
                onHide();
            } else {
                setError(message || "Failed to save data.");
            }
        } catch (err) {
            setError("Network error occurred while saving.");
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    return (
        <KpiModal show={show} onClose={onHide} maxWidth={1080}>
            <ModalHeader
                icon="edit"
                title="Add Data"
                titleStyle={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: "#3346c2",
                    fontFamily: "var(--font-mono)",
                    textTransform: "uppercase",
                    letterSpacing: "0.09em",
                }}
                subtitle={question?.title || question?.question_explanation}
                subtitleStyle={{
                    fontSize: 15.5,
                    fontWeight: 700,
                    color: "var(--ink)",
                    fontFamily: "var(--font-sans)",
                    maxWidth: "none",
                    lineHeight: 1.45,
                    letterSpacing: "-0.01em",
                }}
                bg="linear-gradient(135deg,#f8faff 0%,#f1f5fd 100%)"
                borderColor="#dbe4fd"
                onClose={onHide}
            />

            <div style={{ padding: "16px 24px 24px", overflowY: "auto", flex: 1 }}>
                {error && (
                    <div style={{ padding: "10px 14px", borderRadius: 8, background: "var(--red-2)", border: "1px solid #fca5a5", fontSize: 12, color: "var(--red)", fontWeight: 600, marginBottom: 16 }}>
                        ⚠️ {error}
                    </div>
                )}

                <div style={{ background: "var(--surface)", borderRadius: 12, border: "1px solid var(--rule)", overflow: "hidden", marginBottom: 16 }}>
                    <TabularDynamicForm
                        question={question}
                        gridData={gridData}
                        setGridData={setGridData}
                        columnGroups={columnGroups}
                        setColumnGroups={setColumnGroups}
                        rowNames={rowNames}
                        setRowNames={setRowNames}
                    />
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: showNoteEditor ? 10 : 24 }}>
                    <ViewNoteButton note={matchedAnswer?.note} />
                    <button
                        className="kpi-action-btn kpi-btn-history"
                        onClick={() => setShowNoteEditor((p) => !p)}
                    >
                        <KpiIcon name={showNoteEditor ? "x" : "note"} size={11} />{" "}
                        {showNoteEditor ? "Hide Note" : noteToText(matchedAnswer?.note) ? "Edit Note" : "Add Note"}
                    </button>
                </div>

                {showNoteEditor && (
                    <div style={{ background: "var(--surface-2)", borderRadius: 10, border: "1px solid var(--rule)", padding: "16px", marginBottom: 16 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-mono)", marginBottom: 10 }}>
                            Notes
                        </div>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Add any supporting notes here..."
                            style={{ width: "100%", padding: "10px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13, minHeight: 80, resize: "vertical", fontFamily: "var(--font-sans)" }}
                        />
                    </div>
                )}

                <div style={{ background: "var(--surface-2)", borderRadius: 10, border: "1px solid var(--rule)", padding: "16px", marginBottom: 24 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-mono)", marginBottom: 10 }}>
                        Evidence
                    </div>
                    <MultiDocumentUploadField
                        existingDocuments={existingDocuments}
                        resetKey={`${questionId}-${resolvedFyId}-${show}`}
                        onChange={(next) => { setAttachments(next); setDocsUploading(next.isUploading); }}
                        previewVariant="kpi"
                    />
                </div>

                <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                    <button className="kpi-btn-secondary" disabled={saving} onClick={onHide}>
                        Cancel
                    </button>
                    <button className="kpi-btn-primary" disabled={saving || docsUploading} onClick={handleSave}>
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </KpiModal>
    );
};

export default QualitativeAddDataModal;
